import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Menu, Search, X, Building2, User, AlertTriangle, Calendar, MessageSquare, Clock, Lightbulb, TrendingUp } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_syndic-app-1/artifacts/1ku8mjse_Logo%20Syndicappli_White_2022.png';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Search suggestions for help
const SEARCH_SUGGESTIONS = [
  { text: 'Rechercher un immeuble', example: 'ex: "Résidence Soleil"', icon: Building2 },
  { text: 'Rechercher un coproprietaire', example: 'ex: "Dupont"', icon: User },
  { text: 'Rechercher un incident', example: 'ex: "fuite"', icon: AlertTriangle },
  { text: 'Rechercher une assemblée', example: 'ex: "AG 2026"', icon: Calendar },
];

// Max history items
const MAX_HISTORY_ITEMS = 5;
const SEARCH_HISTORY_KEY = 'syndicappli_search_history';

const MobileHeader = ({ 
  title, 
  onMenuClick, 
  showBackButton = false,
  showSearch = true,
  searchValue = '',
  onSearchChange,
  rightAction
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = useAuth();
  
  // Search state
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchHistory, setSearchHistory] = useState([]);
  const searchTimeoutRef = useRef(null);
  const searchInputRef = useRef(null);
  
  // Determine if we should show back button
  const shouldShowBack = showBackButton || (location.pathname !== '/' && location.key !== 'default');

  // Load search history
  useEffect(() => {
    try {
      const saved = localStorage.getItem(SEARCH_HISTORY_KEY);
      if (saved) {
        setSearchHistory(JSON.parse(saved));
      }
    } catch (e) {
      console.error('Error loading search history:', e);
    }
  }, [searchOpen]);

  // Save to history
  const saveToHistory = (query, result) => {
    if (!query || query.length < 2) return;
    
    const historyItem = {
      query,
      timestamp: Date.now(),
      resultType: result?.type || null,
      resultTitle: result?.title || null,
      url: result?.url || null
    };

    const currentHistory = JSON.parse(localStorage.getItem(SEARCH_HISTORY_KEY) || '[]');
    const filtered = currentHistory.filter(item => item.query.toLowerCase() !== query.toLowerCase());
    const newHistory = [historyItem, ...filtered].slice(0, MAX_HISTORY_ITEMS);
    
    try {
      localStorage.setItem(SEARCH_HISTORY_KEY, JSON.stringify(newHistory));
      setSearchHistory(newHistory);
    } catch (e) {
      console.error('Error saving search history:', e);
    }
  };

  // Clear history
  const clearHistory = () => {
    setSearchHistory([]);
    try {
      localStorage.removeItem(SEARCH_HISTORY_KEY);
    } catch (e) {
      console.error('Error clearing search history:', e);
    }
  };

  // Focus input when search opens
  useEffect(() => {
    if (searchOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [searchOpen]);

  // Search with debounce
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (searchQuery.length >= 2) {
      setSearchLoading(true);
      searchTimeoutRef.current = setTimeout(() => {
        performSearch(searchQuery);
      }, 300);
    } else {
      setSearchResults([]);
    }

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery]);

  const performSearch = async (query) => {
    try {
      const response = await axios.get(`${API}/search`, {
        params: { q: query },
        headers: { Authorization: `Bearer ${token}` }
      });
      setSearchResults(response.data.results || []);
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSearchResultClick = (result) => {
    saveToHistory(searchQuery, result);
    setSearchOpen(false);
    setSearchQuery('');
    setSearchResults([]);
    navigate(result.url);
  };

  const handleHistoryItemClick = (item) => {
    if (item.url) {
      setSearchOpen(false);
      setSearchQuery('');
      setSearchResults([]);
      navigate(item.url);
    } else {
      setSearchQuery(item.query);
    }
  };

  const getSearchIcon = (type) => {
    switch (type) {
      case 'building': return <Building2 size={16} className="text-blue-500" />;
      case 'user': return <User size={16} className="text-green-500" />;
      case 'incident': return <AlertTriangle size={16} className="text-orange-500" />;
      case 'assembly': return <Calendar size={16} className="text-purple-500" />;
      case 'message': return <MessageSquare size={16} className="text-teal-500" />;
      default: return <Search size={16} className="text-gray-400" />;
    }
  };

  const handleBack = () => {
    if (window.history.length > 2) {
      navigate(-1);
    } else {
      navigate('/');
    }
  };

  const handleLogoClick = () => {
    navigate('/');
  };

  const closeSearch = () => {
    setSearchOpen(false);
    setSearchQuery('');
    setSearchResults([]);
  };

  return (
    <>
      <header 
        className="lg:hidden sticky top-0 z-30"
        data-testid="mobile-header"
      >
        {/* Gradient Header */}
        <div 
          className="px-4 pt-3 pb-4"
          style={{
            background: 'linear-gradient(135deg, #00D3A8 0%, #00C7E5 100%)'
          }}
        >
          {/* Top row: Navigation */}
          <div className="flex items-center justify-between">
            {/* Left: Logo (clickable to go home) or Back button */}
            {shouldShowBack ? (
              <button
                onClick={handleBack}
                className="p-2 -ml-2 text-white hover:bg-white/10 rounded-full transition-colors"
                data-testid="mobile-back-btn"
              >
                <ArrowLeft size={24} strokeWidth={2} />
              </button>
            ) : (
              <button
                onClick={handleLogoClick}
                className="flex items-center -ml-1"
                data-testid="mobile-logo-btn"
              >
                <img 
                  src={LOGO_URL} 
                  alt="SyndicAppli" 
                  className="h-8 w-auto"
                />
              </button>
            )}

            {/* Center: Title (only show if not on home or if back button is shown) */}
            {(shouldShowBack || location.pathname !== '/') && (
              <h1 className="text-white text-lg font-semibold flex-1 text-center truncate px-2">
                {title}
              </h1>
            )}
            
            {/* Spacer when on home page without title */}
            {!shouldShowBack && location.pathname === '/' && (
              <div className="flex-1" />
            )}

            {/* Right: Search button + Menu */}
            <div className="flex items-center gap-1">
              {showSearch && (
                <button
                  onClick={() => setSearchOpen(true)}
                  className="p-2 text-white hover:bg-white/10 rounded-full transition-colors"
                  data-testid="mobile-search-btn"
                >
                  <Search size={22} strokeWidth={2} />
                </button>
              )}
              {rightAction || (
                <button
                  onClick={onMenuClick}
                  className="p-2 -mr-2 text-white hover:bg-white/10 rounded-full transition-colors"
                  data-testid="mobile-menu-btn"
                >
                  <Menu size={24} strokeWidth={2} />
                </button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Search Modal */}
      {searchOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/50"
            onClick={closeSearch}
          />
          
          {/* Search Panel */}
          <div className="absolute inset-x-0 top-0 bg-white shadow-xl animate-in slide-in-from-top duration-200">
            {/* Search Input */}
            <div className="p-4 border-b border-gray-100">
              <div className="relative flex items-center gap-3">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Rechercher..."
                    className="w-full pl-10 pr-4 py-3 bg-gray-100 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#00D3A8]/30"
                    data-testid="mobile-search-input"
                  />
                </div>
                <button
                  onClick={closeSearch}
                  className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <X size={20} />
                </button>
              </div>
            </div>

            {/* Search Results */}
            <div className="max-h-[70vh] overflow-y-auto">
              {searchLoading ? (
                <div className="px-4 py-12 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#00D3A8] border-t-transparent mx-auto"></div>
                  <p className="text-sm text-gray-400 mt-3">Recherche en cours...</p>
                </div>
              ) : searchQuery.length >= 2 && searchResults.length === 0 ? (
                <div className="px-4 py-12 text-center text-gray-400">
                  <Search size={32} className="mx-auto mb-3 text-gray-200" />
                  <p className="text-sm">Aucun résultat pour "{searchQuery}"</p>
                </div>
              ) : searchQuery.length >= 2 && searchResults.length > 0 ? (
                <>
                  <div className="px-4 py-2 bg-gray-50 border-b border-gray-100">
                    <span className="text-xs font-medium text-gray-500">
                      {searchResults.length} résultat{searchResults.length > 1 ? 's' : ''}
                    </span>
                  </div>
                  {searchResults.map((result, index) => (
                    <button
                      key={`${result.type}-${result.id}-${index}`}
                      onClick={() => handleSearchResultClick(result)}
                      className="w-full px-4 py-4 hover:bg-gray-50 active:bg-gray-100 transition-colors text-left flex items-center gap-3 border-b border-gray-50"
                    >
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                        {getSearchIcon(result.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-800 truncate">
                          {result.title}
                        </p>
                        <p className="text-xs text-gray-400 truncate">
                          {result.subtitle}
                        </p>
                      </div>
                      <span className="text-[10px] text-gray-400 uppercase px-2 py-1 bg-gray-100 rounded">
                        {result.type === 'building' && 'Immeuble'}
                        {result.type === 'user' && 'Copropriétaire'}
                        {result.type === 'incident' && 'Incident'}
                        {result.type === 'assembly' && 'AG'}
                        {result.type === 'message' && 'Message'}
                      </span>
                    </button>
                  ))}
                </>
              ) : (
                <>
                  {/* Help message when typing 1 character */}
                  {searchQuery.length === 1 && (
                    <div className="px-4 py-3">
                      <div className="flex items-center gap-2 text-amber-600 bg-amber-50 px-3 py-2 rounded-lg">
                        <Lightbulb size={16} />
                        <span className="text-xs">Tapez au moins 2 caractères pour rechercher</span>
                      </div>
                    </div>
                  )}

                  {/* Search History */}
                  {searchHistory.length > 0 && searchQuery.length === 0 && (
                    <>
                      <div className="px-4 py-2 bg-gray-50 border-b border-gray-100 flex items-center justify-between">
                        <span className="text-xs font-medium text-gray-500 flex items-center gap-1.5">
                          <Clock size={12} />
                          Recherches récentes
                        </span>
                        <button
                          onClick={clearHistory}
                          className="text-xs text-gray-400 hover:text-red-500 transition-colors"
                        >
                          Effacer
                        </button>
                      </div>
                      {searchHistory.map((item, index) => (
                        <button
                          key={`history-${index}`}
                          onClick={() => handleHistoryItemClick(item)}
                          className="w-full px-4 py-3 hover:bg-gray-50 active:bg-gray-100 transition-colors text-left flex items-center gap-3 border-b border-gray-50"
                        >
                          <Clock size={16} className="text-gray-300" />
                          <div className="flex-1 min-w-0">
                            <span className="text-sm text-gray-700">{item.query}</span>
                            {item.resultTitle && (
                              <p className="text-xs text-gray-400 truncate">→ {item.resultTitle}</p>
                            )}
                          </div>
                        </button>
                      ))}
                    </>
                  )}

                  {/* Suggestions */}
                  <div className="px-4 py-2 bg-gray-50 border-t border-gray-100">
                    <span className="text-xs font-medium text-gray-500 flex items-center gap-1.5">
                      <TrendingUp size={12} />
                      Suggestions de recherche
                    </span>
                  </div>
                  {SEARCH_SUGGESTIONS.map((suggestion, index) => (
                    <div
                      key={`suggestion-${index}`}
                      className="px-4 py-3 text-left flex items-center gap-3 border-b border-gray-50 last:border-0"
                    >
                      <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center">
                        <suggestion.icon size={16} className="text-gray-400" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm text-gray-600">{suggestion.text}</p>
                        <p className="text-xs text-gray-400">{suggestion.example}</p>
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MobileHeader;
